import type { AxiosResponse } from 'axios';

import { useGetFuelFilling } from '@/generated/client/fuelfilling/fuelfilling';
import type { DetailedFuelFilling } from '@/generated/model';
import { toTimeMicros } from '@/shared/utils/timeConversion';

import type { RefuelsDateRange } from '../types/RefuelsDateRange.types';

type UseRefuelsQueryParams = {
  targetId: number | undefined;
  dates: RefuelsDateRange | undefined;
};

export type RefuelsQuery = ReturnType<typeof useRefuelsQuery>;

const REFUELS_PAGE_SIZE = 1000;
// Refuels must always be fetched fresh, so both cache settings are intentionally zero.
// This also requires an explicit refetch() when RefuelsPopup detects the same date range with isSameDateRange.
const REFUELS_STALE_TIME_MS = 0;
const REFUELS_GC_TIME_MS = 0;

function selectRefuels(response: AxiosResponse<DetailedFuelFilling[]>) {
  return response.data;
}

export function useRefuelsQuery({ targetId, dates }: UseRefuelsQueryParams) {
  const isEnabled = targetId !== undefined && dates !== undefined;

  return useGetFuelFilling(
    // Orval requires a number, but enabled prevents this sentinel from ever reaching the API.
    targetId ?? 0,
    {
      fromTime: dates ? toTimeMicros(dates.from) : 0,
      toTime: dates ? toTimeMicros(dates.to) : 0,
      offset: 0,
      limit: REFUELS_PAGE_SIZE,
    },
    {
      query: {
        enabled: isEnabled,
        staleTime: REFUELS_STALE_TIME_MS,
        gcTime: REFUELS_GC_TIME_MS,
        select: selectRefuels,
        retry: false,
      },
    }
  );
}
